import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  useTheme,
  Alert,
  Snackbar,
  Badge,
  Tabs,
  Tab
} from '@mui/material';
import {
  Visibility,
  Edit,
  CheckCircle,
  Cancel,
  Schedule,
  PriorityHigh,
  Email,
  Person,
  CalendarToday,
  Refresh
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const SupportTicketManager = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { user, isAdmin } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [updateData, setUpdateData] = useState({ status: '', assignedTo: '', resolution: '' });
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    if (isAdmin) {
      fetchTickets();
    }
  }, [isAdmin, filterStatus]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const queryParams = filterStatus !== 'all' ? `?status=${filterStatus}` : '';
      const response = await fetch(`https://yiyuz8psi0.execute-api.ap-south-1.amazonaws.com/prod/api/admin/support/tickets${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTickets(data.tickets || []);
      } else {
        console.error('API Error:', response.status, response.statusText);
        setTickets([]);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setUpdateData({
      status: ticket.status,
      assignedTo: ticket.assignedTo || '',
      resolution: ticket.resolution || ''
    });
    setDialogOpen(true);
  };

  const handleUpdateTicket = async () => {
    if (!selectedTicket) return;
    
    try {
      const response = await fetch(`https://yiyuz8psi0.execute-api.ap-south-1.amazonaws.com/prod/api/admin/support/ticket/${selectedTicket.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(updateData)
      });
      
      if (response.ok) {
        setSuccessMessage('Ticket updated successfully');
        setShowSuccess(true);
        setDialogOpen(false);
        fetchTickets();
      } else {
        console.error('API Error:', response.status, response.statusText);
        setSuccessMessage('Failed to update ticket');
        setShowSuccess(true);
      }
    } catch (error) {
      console.error('Error updating ticket:', error);
      setSuccessMessage('Failed to update ticket');
      setShowSuccess(true);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'error';
      case 'in-progress': return 'warning';
      case 'resolved': return 'success';
      case 'closed': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <Schedule />;
      case 'in-progress': return <Edit />;
      case 'resolved': return <CheckCircle />;
      case 'closed': return <Cancel />;
      default: return <Schedule />;
    }
  };

  const getTicketStats = () => {
    const stats = {
      total: tickets.length,
      open: tickets.filter(t => t.status === 'open').length,
      inProgress: tickets.filter(t => t.status === 'in-progress').length,
      resolved: tickets.filter(t => t.status === 'resolved').length,
      urgent: tickets.filter(t => t.priority === 'urgent').length
    };
    return stats;
  };

  const filteredTickets = tickets.filter(ticket => {
    if (tabValue === 0) return ticket.status === 'open';
    if (tabValue === 1) return ticket.status === 'in-progress';
    if (tabValue === 2) return ticket.status === 'resolved';
    if (tabValue === 3) return ticket.status === 'closed';
    return true;
  });

  if (!isAdmin) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          Access denied. Admin privileges required.
        </Alert>
      </Container>
    );
  }

  const stats = getTicketStats();

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Support Ticket Manager
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchTickets}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ background: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.7)' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary">{stats.total}</Typography>
              <Typography variant="body2">Total Tickets</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ background: isDark ? 'rgba(244,67,54,0.1)' : 'rgba(244,67,54,0.1)' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="error">{stats.open}</Typography>
              <Typography variant="body2">Open</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ background: isDark ? 'rgba(255,152,0,0.1)' : 'rgba(255,152,0,0.1)' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main">{stats.inProgress}</Typography>
              <Typography variant="body2">In Progress</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ background: isDark ? 'rgba(76,175,80,0.1)' : 'rgba(76,175,80,0.1)' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">{stats.resolved}</Typography>
              <Typography variant="body2">Resolved</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ background: isDark ? 'rgba(244,67,54,0.2)' : 'rgba(244,67,54,0.2)' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Badge badgeContent={stats.urgent} color="error">
                <PriorityHigh color="error" />
              </Badge>
              <Typography variant="body2" sx={{ mt: 1 }}>Urgent</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label={`Open (${stats.open})`} />
          <Tab label={`In Progress (${stats.inProgress})`} />
          <Tab label={`Resolved (${stats.resolved})`} />
          <Tab label="Closed" />
        </Tabs>
      </Box>

      {/* Tickets Table */}
      <Card sx={{ background: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.7)' }}>
        <CardContent>
          <TableContainer component={Paper} sx={{ background: 'transparent' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Ticket ID</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTickets.map((ticket) => (
                  <TableRow key={ticket.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {ticket.ticketId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {ticket.subject}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Person fontSize="small" />
                        {ticket.name}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Email fontSize="small" />
                        {ticket.email}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ticket.priority.toUpperCase()}
                        color={getPriorityColor(ticket.priority)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(ticket.status)}
                        label={ticket.status.replace('-', ' ').toUpperCase()}
                        color={getStatusColor(ticket.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarToday fontSize="small" />
                        {new Date(ticket.createdAt).toLocaleDateString()}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleViewTicket(ticket)}
                        color="primary"
                        size="small"
                      >
                        <Visibility />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Ticket Detail Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Ticket Details - {selectedTicket?.ticketId}
        </DialogTitle>
        <DialogContent>
          {selectedTicket && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Name"
                  value={selectedTicket.name}
                  fullWidth
                  disabled
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Email"
                  value={selectedTicket.email}
                  fullWidth
                  disabled
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Subject"
                  value={selectedTicket.subject}
                  fullWidth
                  disabled
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Message"
                  value={selectedTicket.message}
                  fullWidth
                  multiline
                  rows={4}
                  disabled
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={updateData.status}
                    onChange={(e) => setUpdateData({...updateData, status: e.target.value})}
                    label="Status"
                  >
                    <MenuItem value="open">Open</MenuItem>
                    <MenuItem value="in-progress">In Progress</MenuItem>
                    <MenuItem value="resolved">Resolved</MenuItem>
                    <MenuItem value="closed">Closed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  label="Assigned To"
                  value={updateData.assignedTo}
                  onChange={(e) => setUpdateData({...updateData, assignedTo: e.target.value})}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Resolution Notes"
                  value={updateData.resolution}
                  onChange={(e) => setUpdateData({...updateData, resolution: e.target.value})}
                  fullWidth
                  multiline
                  rows={3}
                  margin="normal"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateTicket} variant="contained">
            Update Ticket
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SupportTicketManager;